import * as React from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import Divider from "@mui/material/Divider";

import CustomButton from "../../components/CustomButton";

import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { removeUserToken } from "../../redux/reducer/auth.reducer";
import { firstLetterToUpperCase } from "../../helpers/stringFormater";
import SvgIcon from "../../helpers/SvgIcon";
import AlertsBellMenu from "./AlertsBellMenu";
import { getTodayInfo } from "../../utils/dateAndTime";

const menuId = "primary-search-account-menu";
const mobileMenuId = "primary-search-account-menu-mobile";

const NavbarMenuDesktop = ({
  handleProfileMenuOpen,
  handleMobileMenuOpen,
}: {
  handleProfileMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  handleMobileMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
}) => {
  const {
    user: { username },
  } = useAppSelector((state) => ({
    user: state.user,
  }));

  const { dayOfYear, formattedDate } = getTodayInfo();

  return (
    <div className="d-flex align-items-center w-100">
      <div style={{ flex: 1 }} />

      <div className="text-center fs-5 fw-bolder" style={{ color: "#009ef6" }}>
        <p className="m-0">Ziua {dayOfYear - 1}</p>
        <p className="m-0">{formattedDate}</p>
      </div>

      <div
        className="d-flex justify-content-end align-items-center"
        style={{
          flex: 1,
        }}>
        <div className="d-none d-md-flex align-items-center">
          <AlertsBellMenu />
          <CustomButton onClick={handleProfileMenuOpen}>
            {firstLetterToUpperCase(username)}
          </CustomButton>
        </div>

        <div className="d-flex d-md-none">
          <IconButton
            size="large"
            aria-label="show more"
            aria-controls={mobileMenuId}
            aria-haspopup="true"
            tabIndex={-1}
            onClick={handleMobileMenuOpen}
            color="inherit">
            <SvgIcon type="MORE_ICON" />
          </IconButton>
        </div>
      </div>
    </div>
  );
};

const NavbarSubItem = ({
  anchorEl,
  isMenuOpen,
  handleMenuClose,
}: {
  anchorEl: HTMLElement | null;
  isMenuOpen: boolean;
  handleMenuClose: () => void;
}) => {
  const dispatch = useAppDispatch();

  const handleLogout = () => {
    dispatch(removeUserToken());
    handleMenuClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}>
      <MenuItem onClick={handleLogout}>
        <IconButton
          size="large"
          aria-label="show 17 new notifications"
          color="inherit">
          <SvgIcon type="LOGOUT_ICON" />
        </IconButton>
        Logout
      </MenuItem>
    </Menu>
  );
};

const NavbarMenuMobile = ({
  mobileMoreAnchorEl,
  isMobileMenuOpen,
  handleMobileMenuClose,
}: {
  mobileMoreAnchorEl: HTMLElement | null;
  isMobileMenuOpen: boolean;
  handleMobileMenuClose: () => void;
}) => {
  const dispatch = useAppDispatch();
  const {
    user: { username },
  } = useAppSelector((state) => ({
    user: state.user,
  }));

  const handleLogout = () => {
    dispatch(removeUserToken());
    handleMobileMenuClose();
  };

  return (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}>
      <MenuItem disabled>
        <IconButton
          size="large"
          aria-label="account of current user"
          aria-controls="primary-search-account-menu"
          aria-haspopup="true"
          color="inherit">
          <SvgIcon type="ACCOUNT_CIRCLE" />
        </IconButton>

        {firstLetterToUpperCase(username)}
      </MenuItem>
      <Divider />

      <MenuItem>
        <AlertsBellMenu />
        <p>Notifications</p>
      </MenuItem>
      <MenuItem onClick={handleLogout}>
        <IconButton
          size="large"
          aria-label="show 17 new notifications"
          color="inherit">
          <SvgIcon type="LOGOUT_ICON" />
        </IconButton>
        Logout
      </MenuItem>
    </Menu>
  );
};

const NavbarNavigationItems = () => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] =
    React.useState<null | HTMLElement>(null);

  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  return (
    <Box sx={{ flexGrow: 1 }} className="d-flex align-items-center">
      <NavbarMenuDesktop
        handleProfileMenuOpen={handleProfileMenuOpen}
        handleMobileMenuOpen={handleMobileMenuOpen}
      />

      <NavbarMenuMobile
        mobileMoreAnchorEl={mobileMoreAnchorEl}
        isMobileMenuOpen={isMobileMenuOpen}
        handleMobileMenuClose={handleMobileMenuClose}
      />
      <NavbarSubItem
        anchorEl={anchorEl}
        isMenuOpen={isMenuOpen}
        handleMenuClose={handleMenuClose}
      />
    </Box>
  );
};

export default NavbarNavigationItems;
